import styled from '@emotion/styled'
import classNames from 'classnames'
import { Sidebar } from 'primereact/sidebar'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { ERoles, Employee } from '@/schemaTypes'
import { filterMenuByRole } from './filterMenuByRole'

interface SidebarMenuProps {
  visible: boolean
  onClose: () => void
  currentUser: Employee | null
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  visible,
  onClose,
  currentUser,
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState<
    Record<string, boolean | undefined>
  >({})

  const toggleSubMenu = useCallback((index: string) => {
    setExpandedItems((prevExpandedItems) => ({
      ...prevExpandedItems,
      [index]: !prevExpandedItems[index],
    }))
  }, [])

  const isSubMenuItemActive = useCallback(
    (submenuItems: SubMenuItemProps[]) => {
      return submenuItems.some((subMenuItem) => {
        if (subMenuItem.route && subMenuItem.route === location.pathname) {
          return true
        } else if (
          subMenuItem.items &&
          isSubMenuItemActive(subMenuItem.items)
        ) {
          return true
        }
        return false
      })
    },
    [location.pathname],
  )

  useEffect(() => {
    const calculateActiveSubMenus = (sections: SectionProps[]) => {
      const activeSubMenus: Record<string, boolean> = {}

      sections.forEach((section, index) => {
        section.children &&
          filterMenuByRole(section.children, currentUser).forEach(
            (menuItem) => {
              const submenuActive = isSubMenuItemActive(menuItem.items || [])
              if (submenuActive) {
                activeSubMenus[`${index}${menuItem.label}`] = true
              }
            },
          )
      })
      setExpandedItems(activeSubMenus)
    }

    calculateActiveSubMenus(menuOptions)
  }, [currentUser, isSubMenuItemActive, location])

  const renderSubMenuItems = useCallback(
    (submenuItems: SubMenuItemProps[]) =>
      submenuItems
        .filter((e) => !e.isHidden)
        .map((subMenuItem, subIndex) => (
          <StyledNavLink
            key={subIndex}
            to={subMenuItem.route}
            activeClassName="active-sub"
            style={{ textDecoration: 'none' }}
          >
            <SubMenuItem
              className="flex flex-row content-center items-center pl-12 p-1"
              onClick={onClose}
            >
              {subMenuItem?.icon && (
                <i className={classNames('mx-1', subMenuItem.icon)} />
              )}
              <SubMenuText>{t(subMenuItem.label)}</SubMenuText>
            </SubMenuItem>
          </StyledNavLink>
        )),
    [onClose, t],
  )

  const renderMenuItems = useCallback(
    (menuItem: MenuItemProps, index: number) => {
      const isActive = isSubMenuItemActive(menuItem.items || []) ? 'active' : ''

      if (menuItem.route) {
        return (
          <StyledNavLink
            to={menuItem.route}
            activeClassName="active"
            style={{ textDecoration: 'none' }}
            key={index}
          >
            <MenuItem
              className={classNames(
                'flex flex-row content-center items-center',
                isActive,
                { 'mb-2.5': !menuItem.items },
              )}
              onClick={() => {
                toggleSubMenu(`${index}${menuItem.label}`)
                !menuItem.items && onClose()
              }}
            >
              {menuItem.items && (
                <ExpandIcon
                  className="px-1"
                  expanded={expandedItems[`${index}${menuItem.label}`]}
                >
                  <i className="pi pi-angle-right" />
                </ExpandIcon>
              )}

              <div
                className={classNames(
                  'flex flex-row content-center items-center',
                  { 'ml-6': !menuItem.items },
                )}
              >
                <i className={classNames('mx-1', menuItem.icon)} />
                <MenuItemText>{t(menuItem.label)}</MenuItemText>
              </div>
            </MenuItem>
          </StyledNavLink>
        )
      }

      return (
        <MenuItem
          className={classNames(
            'flex flex-row content-center items-center',
            isActive,
            { 'mb-2.5': !menuItem.items },
          )}
          onClick={() => {
            toggleSubMenu(`${index}${menuItem.label}`)
            !menuItem.items && onClose()
          }}
        >
          {menuItem.items && (
            <ExpandIcon
              className="px-1"
              expanded={!!expandedItems[`${index}${menuItem.label}`]}
            >
              <i className="pi pi-angle-right" />
            </ExpandIcon>
          )}

          <div
            className={classNames('flex flex-row content-center items-center', {
              'ml-6': !menuItem.items,
            })}
          >
            <i className={classNames('mx-1', menuItem.icon)} />
            <MenuItemText>{t(menuItem.label)}</MenuItemText>
          </div>
        </MenuItem>
      )
    },
    [expandedItems, onClose, toggleSubMenu, t, isSubMenuItemActive],
  )

  const menuItems = useMemo(
    () =>
      menuOptions.map((section, index) => {
        const visibleChildren = filterMenuByRole(
          section.children ?? [],
          currentUser,
        )

        // A section without any visible entry does not show its title either.
        if (visibleChildren.length === 0) {
          return null
        }

        return (
          <div key={index}>
            <Title className="uppercase mb-1">{section.title}</Title>
            <div className="mb-8">
              {visibleChildren.map((menuItem, innerIndex) => (
                <React.Fragment key={innerIndex}>
                  {renderMenuItems(menuItem, index)}
                  {menuItem.items && (
                    <SubMenu
                      key={`sub_${innerIndex}`}
                      className="mb-2.5"
                      expanded={!!expandedItems[`${index}${menuItem.label}`]}
                    >
                      {renderSubMenuItems(menuItem.items)}
                    </SubMenu>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )
      }),
    [expandedItems, renderMenuItems, renderSubMenuItems, currentUser],
  )

  return (
    <SidebarStyled visible={visible} onHide={() => onClose()}>
      <div className="flex flex-col">{menuItems}</div>
    </SidebarStyled>
  )
}

type SubMenuItemProps = {
  label: string
  route?: string
  icon?: string
  items?: SubMenuItemProps[]
  isHidden?: boolean
  /** Overrides the minRole of the parent entry for this sub-item. */
  minRole?: ERoles
}

type MenuItemProps = {
  label: string
  route?: string
  icon?: string
  items?: SubMenuItemProps[]
  isHidden?: boolean
  /** Lowest role that sees this entry (see EMPLOYEE_ROLE_HIERARCHY). Sub-items inherit it unless they set their own. */
  minRole: ERoles
}

type SectionProps = {
  title: string
  children: MenuItemProps[]
}

const menuOptions: SectionProps[] = [
  {
    title: 'Shop',
    children: [
      {
        label: 'deal.plural',
        route: '/inApp/deals',
        minRole: ERoles.Guest,
        icon: 'pi pi-fw pi-shopping-cart',
      },
      {
        label: 'custom_deal.plural',
        route: '/inApp/customDeals',
        minRole: ERoles.Locationstaff,
        icon: 'pi pi-fw pi-shopping-bag',
      },
      {
        label: 'item.plural',
        route: '/inApp/items',
        minRole: ERoles.Guest,
        icon: 'pi pi-fw pi-pencil',
      },
      {
        label: 'auctions',
        route: '/inApp/auctions',
        minRole: ERoles.Locationadmin,
        icon: 'pi pi-fw pi-megaphone',
      },
      {
        label: 'private_sales',
        route: '/inApp/privatesales',
        minRole: ERoles.Admin,
        icon: 'pi pi-fw pi-percentage',
      },
      {
        label: 'customers',
        route: '/inApp/customers',
        minRole: ERoles.Locationstaff,
        icon: 'pi pi-fw pi-users',
      },
      {
        label: 'blacklists',
        route: '/inApp/customerBlacklists',
        minRole: ERoles.Locationstaff,
        icon: 'pi pi-fw pi-ban',
      },
      {
        label: 'cash_book.label',
        minRole: ERoles.Locationstaff,
        icon: 'pi pi-fw pi-money-bill',
        items: [
          {
            label: 'revenues_and_expenses',
            route: '/inApp/cashBook/cashFlows',
          },
          {
            label: 'bills_and_coins_count',
            route: '/inApp/cashBook/counts',
          },
        ],
      },
    ],
  },
  {
    title: 'Admin',
    children: [
      {
        label: 'import_export',
        minRole: ERoles.Superadmin,
        icon: 'pi pi-fw pi-file-import',
        items: [
          {
            label: 'imports_and_exports',
            route: '/inApp/importExport/products',
          },
        ],
      },
      {
        label: 'products',
        minRole: ERoles.Admin,
        icon: 'pi pi-fw pi-database',
        items: [
          {
            label: 'products',
            route: '/inApp/products',
          },
          {
            label: 'properties',
            route: '/inApp/product-property-definitions',
          },
          {
            label: 'crawler_config',
            route: '/inApp/productconfigurator',
            minRole: ERoles.Superadmin,
          },
        ],
      },
      {
        label: 'configurations',
        minRole: ERoles.Superadmin,
        icon: 'pi pi-fw pi-wrench',
        items: [
          {
            label: 'loan.loan_to_values',
            route: '/inApp/configurations/loanToValues',
          },
          {
            label: 'fees.label',
            route: '/inApp/configurations/fees',
          },
          {
            label: 'material.material_prices',
            route: '/inApp/configurations/materialPrices',
          },
          {
            label: 'shop.plural',
            route: '/inApp/configurations/shops',
          },
          {
            label: 'company.plural',
            route: '/inApp/configurations/companies',
          },
          {
            label: 'storage.storage_facilities',
            route: '/inApp/configurations/storageFacilities',
            minRole: ERoles.Admin,
          },
          {
            label: 'employees',
            route: '/inApp/configurations/employees',
          },
          {
            label: 'item_questions',
            route: '/inApp/configurations/itemQuestions',
            minRole: ERoles.Admin,
          },
          {
            label: 'item_categories.label',
            route: '/inApp/configurations/itemCategories',
            minRole: ERoles.Admin,
          },
          {
            label: 'response_templates',
            route: '/inApp/configurations/responseTemplates',
            minRole: ERoles.Admin,
          },
          {
            label: 'custom_deal_form.plural',
            route: '/inApp/configurations/customDealForms',
            minRole: ERoles.Admin,
          },
          {
            label: 'refurbed_offers',
            route: '/inApp/configurations/refurbedOffers',
            minRole: ERoles.Admin,
          },
          {
            label: 'maintenance',
            route: '/inApp/configurations/maintenance',
          },
        ],
      },
    ],
  },
]

const SidebarStyled = styled(Sidebar)`
  &&& {
    width: 17.25rem;
    .p-sidebar-header {
      padding: 1rem;
    }
    .p-sidebar-content {
      padding: 1rem;
    }
  }
`

const Title = styled.p`
  color: var(--global-text-color, #495057);
  font-family: Inter;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
  line-height: 0.875rem;
`

const MenuItem = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  position: relative;
  height: 2.625rem;
  &:hover {
    background: #e9ecef;
  }
  &.active {
    color: #6366f1;
  }
`

const MenuItemText = styled.p`
  font-family: Inter;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`

const SubMenu = styled.div<{ expanded: boolean }>`
  overflow: hidden;
  max-height: ${({ expanded }) => (expanded ? '700px' : '0')};
  transition: max-height 0.5s ease-in-out;
`

const SubMenuText = styled.p`
  font-family: Inter;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`

const SubMenuItem = styled.div`
  cursor: pointer;
  height: 2.625rem;
  &:hover {
    background: #e9ecef;
  }
`

const StyledNavLink = styled(NavLink)`
  color: var(--menuitem-color, #495057);
  &.active {
    color: #6366f1;
  }
  &.active-sub {
    color: #6366f1;
  }
`

const ExpandIcon = styled.span<{ expanded: boolean }>`
  font-size: 14px;
  transform: translateY(0%);
  transition: transform 0.5s ease-in-out;
  ${({ expanded }) => expanded && `transform: translateY(0%) rotate(90deg);`}
`

export default SidebarMenu
